import React from 'react';
import {view} from '@sail/ui';
import {message, useIntl} from '@sail/intl';
import {redactWalletAddress} from 'src/utils/helpers';
import {Text} from 'src/v2/components/common';
import {WalletIcon} from 'src/v2/components/wallet/WalletIcon';
import {NetworkBadge} from 'src/v2/components/wallet/NetworkBadge';
import type {WalletOption} from 'src/v2/state/types';
import type {SupportedNetwork} from 'src/types';

const messages = {
  connectedWallet: message({
    id: 'crypto.payins.v2.connected_wallet_card.title',
    description: 'Label above the connected wallet card',
    defaultMessage: 'Connected wallet',
  }),

  available: message<{balance: string}>({
    id: 'crypto.payins.v2.connected_wallet_card.available_balance',
    description: 'The balance available in the connected wallet',
    defaultMessage: 'Available: {balance}',
  }),
};

export interface ConnectedWalletCardProps {
  /** The connected wallet: its provider option (for the icon), address, and network. */
  wallet: {
    option: WalletOption;
    /** Raw wallet address; redacted for display, e.g. `BV••1s1y`. */
    address: string;
    /** The network the wallet is connected on, e.g. `base`. */
    network: SupportedNetwork;
  };
  /** The available balance, formatted with its asset code, e.g. `1.24195 ETH`. */
  availableBalance?: string | null;
  /** Optional overflow menu rendered at the trailing edge (e.g. WalletActionMenu). */
  actionMenu?: React.ReactNode;
}

export const ConnectedWalletCard: React.FC<ConnectedWalletCardProps> = ({
  wallet: {option, address, network},
  availableBalance,
  actionMenu,
}) => {
  const intl = useIntl();

  return (
    <view.div css={{stack: 'y', gapY: 'space.150'}}>
      <Text type="bodyEmphasized">
        {intl.formatMessage(messages.connectedWallet)}
      </Text>
      <view.div
        css={{
          stack: 'x',
          gapX: 'small',
          alignY: 'center',
          borderRadius: 'small',
          borderColor: 'border',
          borderWidth: 'small',
          borderStyle: 'solid',
          padding: 'space.150',
        }}
      >
        <WalletIcon
          walletOption={option}
          width="32px"
          height="32px"
          borderRadius="8px"
        />
        <view.div css={{stack: 'y'}}>
          <Text type="labelEmphasized">{redactWalletAddress(address)}</Text>
          {availableBalance ? (
            <Text type="caption" css={{color: 'subdued'}}>
              {intl.formatMessage(messages.available, {
                balance: availableBalance,
              })}
            </Text>
          ) : null}
        </view.div>
        <NetworkBadge network={network} />
        <view.div css={{width: 'fit'}}>{actionMenu}</view.div>
      </view.div>
    </view.div>
  );
};
